/**
 * Bundle streaming for a series' instance page.
 *
 * The cold-open cost of a series is not decoding, it is the fan-out: one HTTPS
 * request per instance, several hundred of them, each paying connection setup
 * and per-object latency. StorageProxy's `/bundle/v1/{manifest}` route collapses
 * a run of those objects into a single streamed response; this module drives it
 * from the browser and drops the bytes into the wadouri dataset cache, so the
 * prefetcher's subsequent `loadAndCacheImage` calls hit memory instead of the
 * network. The prefetch window is untouched — it stops being a download
 * scheduler and becomes a decode scheduler.
 *
 * Everything here is best-effort. Bundling is an optimization layered over a
 * per-object path that still works perfectly, and the fallback needs no code at
 * all: an ordinal we fail to seed is simply an imageId whose dataset is not
 * cached, which the loader fetches the way it always did. That is what makes the
 * circuit breaker cheap — when the signing key has rotated or the stream is
 * garbage, the right move is to stop trying for this series and let the boring
 * path carry the study.
 *
 * Which is why most of the interesting code here is about NOT tripping that
 * breaker. Three things look like faults and are not: load shedding, a chunk
 * too big for the proxy's body cap, and a stream the proxy deliberately cut
 * short. Each settles the affected chunk alone and leaves the series bundling.
 * See "Three-way failure classification" on {@link loadSeriesBundles}.
 *
 * Concurrency is not this module's to decide either. Every chunk fetch takes a
 * slot from the process-wide gate in ./bundleQueue first, because the proxy's
 * admission cap is shared across every reader on the instance and a single tab
 * running its own per-page pools could exceed it alone.
 */
import * as telemetry from '@/lib/telemetry';
import { BUNDLE_STATUS_OK, BundleStreamError, BundleStreamParser } from './bundleFrameParser';
import { acquireBundleSlot } from './bundleQueue';
import { seedDataSet, unseedDataSet } from './datasetSeed';
/**
 * How long a chunk may go without delivering a byte before it is abandoned.
 *
 * This is load-bearing, not belt-and-braces. It no longer holds a viewport
 * hostage — rows reach the viewer before any chunk is asked for — but a stalled
 * stream still holds a slot out of a pool of four, and it still leaves the
 * chunk's ids marked as "bytes on the way" so the prefetch window declines to
 * fetch them per object. Left alone, one dead socket therefore quietly stops a
 * quarter of the study's transport and strands the images it claimed.
 *
 * A stall breaks the circuit rather than failing the chunk alone, and that is
 * deliberate: it is the one symptom that costs the reader half a minute before
 * it is even observed, so paying it once per series is the most a study should
 * ever spend on it. Now that chunks share a small global slot pool, a stalled
 * stream also holds a slot the whole time — one more reason not to line up
 * another one behind it.
 *
 * Exported for its own tests, which cannot afford to wait it out for real.
 */
export const CHUNK_STALL_TIMEOUT_MS = 30_000;
/**
 * How many times a shed chunk (HTTP 429) is asked for again before it gives up
 * and hands its images to the per-object path.
 *
 * Small on purpose. The proxy's admission gate clears in about a second under
 * normal contention, so three attempts cover it; past that the queue is deep
 * enough that waiting longer costs the reader more than just fetching the
 * objects individually, which is what would happen anyway.
 */
const SHED_RETRY_LIMIT = 3;
/** Wait applied when a 429 carries no usable `Retry-After`. */
const SHED_RETRY_DEFAULT_MS = 1_000;
/**
 * Ceiling on the wait, however long the header asks for. A chunk waiting out a
 * backoff is a chunk whose ids the prefetch window will not request per object
 * (see `markChunkIdsAttempted` in lib/cornerstone/prefetchManager), so an
 * honoured `Retry-After: 120` would park two minutes of the series' scroll-ahead
 * on a stream that has not started.
 */
const SHED_RETRY_MAX_MS = 5_000;
/**
 * How many times a chunk whose stream ended early is asked for again.
 *
 * ONE, and the asymmetry with {@link SHED_RETRY_LIMIT} is the point. A shed
 * chunk was never served, so re-asking is nearly free and the wait is what the
 * proxy told us to wait. A truncated chunk was served and cut, so the retry
 * re-downloads every byte that already arrived, holding a slot and its ids for
 * the whole of it. One attempt is worth that because a re-request may land on a
 * healthy replica; a second is not, because by then the per-object path would
 * have finished.
 */
const TRUNCATION_RETRY_LIMIT = 1;
/**
 * Wait before re-asking for a truncated chunk. Long enough that an instance
 * mid-restart is not hit again on the same breath, short enough to stay under
 * what the per-object fallback would have cost anyway.
 *
 * Exported for its own tests, alongside {@link CHUNK_STALL_TIMEOUT_MS}.
 */
export const TRUNCATION_RETRY_DELAY_MS = 1_000;
/** The proxy's admission gate: over its concurrent-stream cap, come back later. */
const HTTP_TOO_MANY_REQUESTS = 429;
/** The proxy's body cap: this manifest declares more bytes than it will serve. */
const HTTP_PAYLOAD_TOO_LARGE = 413;
/** What an expired manifest signature comes back as. */
const HTTP_FORBIDDEN = 403;
/**
 * A fault the loader raised itself, carrying the breaker bucket it belongs to
 * as DATA rather than as prose.
 *
 * Reading the taxonomy back out of a message string looked fine and was not.
 * The classifier runs on whatever the failing await REJECTED with, and an
 * aborted body read is free to reject with a generic `AbortError` that says
 * nothing about why it was aborted — so a stalled stream would be filed as a
 * transport failure or not depending on the runtime, which is the one thing a
 * classification must never depend on. The reason is therefore attached where
 * it is actually KNOWN: the stall timer knows it fired, the status check knows
 * it read a status. Messages go back to being for humans.
 */
class BundleFaultError extends Error {
    reason;
    constructor(message, reason) {
        super(message);
        this.name = 'BundleFaultError';
        this.reason = reason;
    }
}
/**
 * Series whose bundling has been switched off for the rest of the session.
 *
 * Session-scoped and deliberately never cleared on success: a bundle failure is
 * almost always systemic (the signing key rotated, an intermediary is mangling
 * the response), so retrying per page would just spend the same latency again
 * before falling back anyway. Everything that is NOT systemic — shedding,
 * oversize chunks, truncation, cancellation — is kept out of here on purpose.
 */
const disabledSeries = new Set();
/**
 * Chunk manifests whose stream is running RIGHT NOW, keyed by manifest URL.
 *
 * Two transports pull chunks and they can want the same one at the same moment.
 * A demand page delivery hands its rows to the viewer BEFORE it fetches
 * anything (see lib/viewer/instancePageDelivery), which is what lets a pane
 * paint its first slice off a single per-object request — but it also means the
 * prefetcher is registered against those ids while the delivery's own chunks
 * are still on the wire, and its window will reach a chunk that is already
 * being streamed. Opening a second stream for it would re-download the whole
 * chunk: a handful of doubled IMAGES is the trade the rows-first delivery makes
 * deliberately, a doubled chunk is not.
 *
 * A URL rather than a series or a page, because that is the granularity a
 * duplicate would actually be at, and because it is the key both sides already
 * hold — `chunkFor` in ./bundleRegistry returns the entry whose `chunk.url`
 * this is, and the prefetcher's fetched-chunk memo is keyed the same way.
 *
 * Entries live for exactly one stream: registered as it starts, removed when it
 * settles, however it settles. A cancelled chunk is therefore freshly fetchable
 * the moment it settles `skipped`, which is the same "a cancel is not a
 * verdict" rule the breaker and the prefetcher's memo both keep.
 */
const inFlightChunks = new Map();
/**
 * The settle promise of a chunk this module is streaming right now, or
 * `undefined` when nothing is.
 *
 * Await it instead of asking for the same manifest again. The promise resolves
 * to the very result the stream's own caller gets — including `skipped`, which
 * says the stream was abandoned and nothing can be concluded from it.
 */
export function inFlightChunk(url) {
    return inFlightChunks.get(url);
}
/** True when this series has tripped its breaker earlier in the session. */
export function isSeriesBundlingDisabled(seriesId) {
    return disabledSeries.has(seriesId);
}
/**
 * Forget every breaker, and every in-flight registration. Call when the study
 * changes, and between tests.
 *
 * Dropping the in-flight table does not stop anything — a stream still running
 * from the outgoing study keeps running and still settles its own handle — it
 * only stops the incoming study from being told about it. Which is correct:
 * its manifests are its own, freshly minted, and nothing it asks for can be
 * satisfied by a stream for another study's URL.
 */
export function resetBundleLoaderState() {
    disabledSeries.clear();
    inFlightChunks.clear();
}
/**
 * Whether an instance page's images should be bundle-streamed at all.
 *
 * Three gates, and the LAN one matters most. `imageIdSourceUrl` builds imageIds
 * from the LAN URL whenever the study's clinic serves DICOM locally, so on those
 * studies the bundle would pull every object across the WAN a second time —
 * bytes the viewer is about to read off the local network for free. Skipping is
 * strictly better there.
 */
export function shouldBundlePage(options) {
    const { seriesId, images, bundles, isVideo, isMicroscopy } = options;
    if (images.length === 0)
        return false;
    // The two routing gates come first — ahead of "did the server send chunks" —
    // because they answer a second question as well, and that one has to be
    // answered whether or not bundling is switched on at all: is this series'
    // content WAN-served raster? `object_bytes` prices every unbundled object at
    // a flat per-image raster estimate, which is wrong by an order of magnitude
    // for a clip or a whole-slide pyramid and wrong by the whole of it for a
    // study the clinic serves off its own network. This predicate is the only
    // place that verdict is reached once per series on every delivery path, so
    // it is where telemetry hears about it.
    // Video (MPEG) and whole-slide microscopy never enter the Cornerstone raster
    // pipeline, so nothing would ever read the datasets a bundle seeded.
    if (isVideo === true || isMicroscopy === true) {
        telemetry.recordSeriesNotWanRaster(seriesId);
        return false;
    }
    // Any LAN-capable instance means the imageIds — and therefore the cache keys —
    // are LAN-shaped for this study. See the note above.
    if (images.some((image) => Boolean(image.localStorageLocation))) {
        telemetry.recordSeriesNotWanRaster(seriesId);
        return false;
    }
    // Bundling disabled server-side, or the series' breaker has tripped. Neither
    // says anything about where the bytes come from, only about this transport.
    if (bundles.length === 0)
        return false;
    if (isSeriesBundlingDisabled(seriesId))
        return false;
    return true;
}
/**
 * Start streaming a page's chunks, returning one handle per chunk in OFFSET
 * order (the order the page's images appear in).
 *
 * Every chunk is started at once and each waits its turn at the global slot
 * gate, which is what actually bounds concurrency — a page does not run a pool
 * of its own, because several pages and several series stream at the same time
 * and only a process-wide number can bound the tab.
 *
 * The handles settle independently, and no caller's rows depend on them: a page
 * is handed to the viewer before this is even called (see
 * lib/viewer/instancePageDelivery). What a settle reports is whether the
 * chunk's bytes are in the dataset cache, which decides only whether the loads
 * that follow are decode work or fetches.
 *
 * ─── Three-way failure classification ──────────────────────────────────────
 *
 * Everything that can go wrong with a chunk lands in exactly one of three
 * buckets, and what separates them is what the SERIES does next:
 *
 * 1. TRUNCATION → one retry, then per object. The stream ended before its
 *    trailer: a clean EOF mid-stream, a cut inside a frame, or a transport
 *    error raised while reading a body that had already answered 2xx. This is
 *    an expected transient rather than a fault, because StorageProxy commits
 *    cacheable response headers before it knows every member will be readable
 *    — once they are out it can no longer write a failure frame without
 *    poisoning the CDN entry for everyone, so it aborts the connection instead.
 *    The breaker stays closed.
 *
 * 2. SHED or OVERSIZE → `failed`, breaker stays closed. A 429 past its retries
 *    is the admission gate saying "not right now"; a 413 is the 64 MiB body cap
 *    saying "not this many objects at once", which a chunk of large MR or US
 *    objects can reach. Both are facts about THIS chunk, so its images go per
 *    object while the series' remaining chunks and later pages still bundle.
 *
 * 3. MALFORMED or PERMANENT → breaker. Bad magic, an out-of-order frame index,
 *    an impossible declared length, a frame mapping past the page's images, a
 *    non-2xx that is not 429 or 413, a transport failure on the REQUEST itself,
 *    or a socket that opens and then stalls. These say the bundle path is not
 *    working for this series at all — so it stops trying and the per-object
 *    path carries the study. The one exception is the status half of it on a
 *    RETAINED manifest, which is a fact about the manifest's age rather than
 *    about the series; see `retained` on {@link LoadSeriesBundlesOptions}.
 *
 * The distinction that needs care is between 1 and 3: a well-formed trailer
 * naming bad ordinals is NEITHER. The stream did its job and told us which
 * members to distrust, so those are retracted and counted failed and the chunk
 * is `seeded`. It is malformed frame CONTENT — a stream that is not a bundle,
 * or is framed wrong — that is the protocol fault.
 *
 * A caller's abort sits outside the taxonomy entirely; see `signal` on
 * {@link LoadSeriesBundlesOptions}.
 */
export function loadSeriesBundles(options) {
    const { seriesId, images, bundles, lane = 'demand', retained = false, signal, fetchImpl = globalThis.fetch.bind(globalThis), } = options;
    const ordered = [...bundles].sort((a, b) => a.offset - b.offset);
    const resolvers = [];
    const handles = ordered.map((chunk) => ({
        chunk,
        settled: new Promise((resolve) => {
            resolvers.push(resolve);
        }),
    }));
    // Published before a byte moves, because a duplicate would be issued in
    // exactly that window: `run` below has not reached its first await yet, and
    // the prefetcher decides whether to open its own stream synchronously. The
    // removal is guarded on identity so a manifest re-minted and re-streamed
    // since cannot be evicted by the older stream finishing.
    handles.forEach((handle) => {
        const { url } = handle.chunk;
        inFlightChunks.set(url, handle.settled);
        void handle.settled.then(() => {
            if (inFlightChunks.get(url) === handle.settled)
                inFlightChunks.delete(url);
        });
    });
    const controllers = new Set();
    const settledChunks = new Set();
    let broken = false;
    // What each chunk actually put in the dataset cache, and what those frames
    // weighed. Owned out here rather than inside `loadChunk` for one reason: a
    // chunk that is ABANDONED never returns through `loadChunk` at all, and its
    // partial credit is real — the frames it parsed before the cut are cached and
    // the per-object path will find them there. Settling those at zero reported a
    // failure that had not happened.
    const seededByPosition = ordered.map(() => new Map());
    const seededBytesByPosition = ordered.map(() => new Map());
    function settle(position, result) {
        if (settledChunks.has(position))
            return;
        settledChunks.add(position);
        // Single-sourced here, at the one point every transport converges on. A
        // page delivered on demand and a chunk the prefetcher fetched settle
        // through this same function, so anything recorded at a CALL SITE counts
        // that caller's chunks and nobody else's — which is how a series bundled
        // end to end at delivery managed to report zero utilization.
        //
        // `skipped` is the one status that is not a verdict: the chunk was never
        // fetched, or the caller withdrew mid-stream and a later pass will fetch it
        // again and report what IT finds. Recording both would count the abandoned
        // stream's partial credit twice and charge its unfetched remainder as a
        // failure, and the transport's own memo already declines to draw a
        // conclusion from a cancel for exactly that reason.
        if (result.status !== 'skipped')
            telemetry.recordBundleChunkResult(seriesId, result);
        resolvers[position](result);
    }
    /** Give up on everything still outstanding, with a reason to abort it by. */
    function abandonAll(reason) {
        controllers.forEach((controller) => {
            controller.abort(reason);
        });
        for (let i = 0; i < ordered.length; i++) {
            settle(i, chunkResult(ordered[i], 'skipped', seededByPosition[i].size));
        }
    }
    /** Trip the breaker: this series stops bundling for the rest of the session. */
    function breakCircuit(reason) {
        if (broken)
            return;
        broken = true;
        disabledSeries.add(seriesId);
        console.warn(`[bundles] disabling bundle streaming for series ${seriesId}`, reason);
        telemetry.recordBundleBreakerReason(classifyBreakerReason(reason), true);
        abandonAll(reason);
    }
    if (signal) {
        if (signal.aborted) {
            abandonAll(signal.reason);
            return handles;
        }
        signal.addEventListener('abort', () => {
            abandonAll(signal.reason);
        }, { once: true });
    }
    async function run(position, chunk) {
        const seededOrdinals = seededByPosition[position];
        try {
            settle(position, await loadChunk({
                chunk,
                images,
                fetchImpl,
                controllers,
                lane,
                retained,
                seededOrdinals,
                seededBytes: seededBytesByPosition[position],
            }));
        }
        catch (error) {
            // Cancellation first, and unconditionally: the caller withdrawing its
            // interest says nothing about whether bundling works for this series —
            // and whatever it had already seeded is still in the cache, so the count
            // it settles with is what arrived, not zero.
            if (signal?.aborted) {
                settle(position, chunkResult(chunk, 'skipped', seededOrdinals.size));
                return;
            }
            if (broken)
                return;
            // A THROW is the systemic signal — a permanent status, a transport
            // failure, a stall, a stream that is not a bundle — so the rest of the
            // series goes with it. Everything that merely gave up (shed past its
            // retries, oversize, truncated past its retry, a retained manifest the
            // proxy refused) returns a `failed` result instead and never reaches here.
            // The partial credit stands even so: a framing fault found halfway down a
            // stream does not un-cache the frames above it.
            settle(position, chunkResult(chunk, 'failed', seededOrdinals.size));
            breakCircuit(error);
        }
    }
    ordered.forEach((chunk, position) => {
        void run(position, chunk);
    });
    return handles;
}
/**
 * Build a chunk's result. `failedCount` is always derived rather than tallied,
 * because "covered but not in the cache" is the only definition the per-object
 * fallback cares about — a member the stream never sent, one it sent as
 * NOT_FOUND, one the trailer retracted and one whose bytes would not parse are
 * all the same thing to the loader that has to fetch it.
 */
function chunkResult(chunk, status, seededCount) {
    return {
        chunkIndex: chunk.index,
        offset: chunk.offset,
        count: chunk.count,
        status,
        seededCount,
        failedCount: Math.max(0, chunk.count - seededCount),
    };
}
/**
 * Classify a breaker-tripping error into the taxonomy telemetry tracks, by what
 * the error IS rather than by what it says.
 *
 * Only two kinds of error can reach here with a reason attached: the parser's
 * `BundleStreamError`, which is a framing disagreement by construction, and the
 * loader's own {@link BundleFaultError}, which was tagged where the fault was
 * detected. Anything else came out of `fetch` or the stream unannotated — a DNS
 * failure, a CORS rejection, a socket reset on the request — and transport is
 * the honest bucket for it.
 */
function classifyBreakerReason(error) {
    if (error instanceof BundleStreamError)
        return 'framing';
    if (error instanceof BundleFaultError)
        return error.reason;
    return 'transport';
}
/**
 * How long to wait before re-asking for a shed chunk.
 *
 * `Retry-After` has two wire forms; only delta-seconds is read. The HTTP-date
 * form would have to be differenced against the client's clock, and a browser
 * whose clock is minutes off would turn a one-second backoff into a stall or a
 * hot retry — for a header the proxy never sends in that form. Anything that is
 * not a plain non-negative number falls back to the default.
 *
 * Exported for its own tests: it is the parsing, not the waiting, that has the
 * interesting cases.
 */
export function shedRetryDelayMs(response) {
    const header = response.headers?.get('Retry-After');
    const seconds = header === null || header === undefined ? NaN : Number(header);
    if (!Number.isFinite(seconds) || seconds < 0)
        return SHED_RETRY_DEFAULT_MS;
    return Math.min(seconds * 1000, SHED_RETRY_MAX_MS);
}
/** Sleep, cut short if `signal` aborts — so breaking the circuit is not delayed. */
function delay(ms, signal) {
    if (signal.aborted)
        return Promise.resolve();
    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        const onAbort = () => {
            clearTimeout(timer);
            resolve();
        };
        signal.addEventListener('abort', onAbort, { once: true });
    });
}
/**
 * Fetch and seed one chunk, retrying the two transient outcomes and throwing on
 * anything that should trip the breaker. See the classification on
 * {@link loadSeriesBundles} for which is which.
 *
 * The chunk's AbortController spans every attempt — the breaker and the caller
 * both cancel through it — while a slot is held for one attempt only.
 */
async function loadChunk(args) {
    const { chunk, images, fetchImpl, controllers, lane, retained, seededOrdinals, seededBytes } = args;
    const controller = new AbortController();
    controllers.add(controller);
    const totalSeededBytes = () => {
        let total = 0;
        for (const bytes of seededBytes.values())
            total += bytes;
        return total;
    };
    try {
        let shedRetries = 0;
        let truncationRetries = 0;
        for (;;) {
            const attempt = await runChunkAttempt({
                chunk,
                images,
                fetchImpl,
                controller,
                lane,
                retained,
                seededOrdinals,
                seededBytes,
            });
            if (attempt.kind === 'complete') {
                telemetry.recordBundleBytes(totalSeededBytes());
                return chunkResult(chunk, 'seeded', seededOrdinals.size);
            }
            // Over the proxy's body cap: no number of retries makes this manifest
            // smaller, so the chunk goes per object immediately. The proxy rejects
            // an oversize chunk pre-body, so there is never anything to add to
            // bundle_bytes here.
            if (attempt.kind === 'oversize') {
                telemetry.recordBundleBreakerReason('413', false);
                return chunkResult(chunk, 'failed', seededOrdinals.size);
            }
            // A status the proxy returned for a manifest signed long ago (see
            // `retained`). Chunk-level, breaker closed, and no retry: nothing about
            // re-asking makes a signature younger.
            if (attempt.kind === 'rejected') {
                if (attempt.status === HTTP_FORBIDDEN)
                    telemetry.recordManifestExpiredSkip();
                telemetry.recordBundleBreakerReason('permanent-status', false);
                return chunkResult(chunk, 'failed', seededOrdinals.size);
            }
            if (attempt.kind === 'shed') {
                // Every shed, retried or not — the numerator for bundle_shed_rate.
                telemetry.recordBundleShed();
                if (shedRetries++ >= SHED_RETRY_LIMIT) {
                    telemetry.recordBundleBreakerReason('shed-exhausted', false);
                    return chunkResult(chunk, 'failed', seededOrdinals.size);
                }
                await delay(attempt.retryAfterMs, controller.signal);
                continue;
            }
            if (truncationRetries++ >= TRUNCATION_RETRY_LIMIT) {
                telemetry.recordBundleBreakerReason('truncation-exhausted', false);
                telemetry.recordBundleBytes(totalSeededBytes());
                return chunkResult(chunk, 'failed', seededOrdinals.size);
            }
            await delay(TRUNCATION_RETRY_DELAY_MS, controller.signal);
        }
    }
    catch (error) {
        // The CONTROLLER is the authority on why this chunk ended, not the
        // rejection that surfaced it. The stall timer tags its abort at the timer,
        // while the read or the request it cancels is free to reject with a generic
        // `AbortError` carrying nothing — so re-throw what we aborted WITH whenever
        // that is one of our own faults, and let the raw rejection stand otherwise
        // (a caller's cancel, a genuine transport error, a framing fault).
        const abortReason = controller.signal.reason;
        const fault = abortReason instanceof BundleFaultError ? abortReason : error;
        // Cancel THIS request before the finally unregisters its controller. The
        // breaker aborts everything still in the set, and by the time it runs this
        // one is out of it — so a framing error, which is thrown with the body
        // still open, would otherwise leave the stream reading to no end.
        controller.abort(fault);
        throw fault;
    }
    finally {
        controllers.delete(controller);
    }
}
/** One network attempt: acquire a slot, fetch, read the stream, release. */
async function runChunkAttempt(args) {
    const { chunk, images, fetchImpl, controller, lane, retained, seededOrdinals, seededBytes } = args;
    // A slot is held for exactly one attempt, so a chunk waiting out a shed
    // backoff or a truncation retry hands its slot back first — the wait costs
    // the tab nothing. Acquiring before the stall timer is armed matters for the
    // same reason in reverse: queueing for admission is not a stall.
    const slot = await acquireBundleSlot(lane, controller.signal);
    let stallTimer;
    const clearStallTimer = () => {
        if (stallTimer !== undefined)
            clearTimeout(stallTimer);
        stallTimer = undefined;
    };
    const armStallTimer = () => {
        clearStallTimer();
        stallTimer = setTimeout(() => {
            // Tagged HERE, where the stall is known to have happened. Nothing
            // downstream can work it out: the abort surfaces as whatever the pending
            // fetch or read chooses to reject with.
            controller.abort(new BundleFaultError(`bundle chunk ${chunk.index} stalled`, 'stall'));
        }, CHUNK_STALL_TIMEOUT_MS);
    };
    try {
        // The stall timeout covers the request as well as the stream: a socket that
        // opens and never answers has to end somewhere.
        armStallTimer();
        // No credentials and no headers: the signed manifest in the path IS the
        // authorization, and sending cookies or an Authorization header would only
        // fragment the CDN cache (or trip CORS) for no gain.
        const response = await fetchImpl(chunk.url, {
            credentials: 'omit',
            signal: controller.signal,
        });
        if (response.status === HTTP_TOO_MANY_REQUESTS) {
            return { kind: 'shed', retryAfterMs: shedRetryDelayMs(response) };
        }
        if (response.status === HTTP_PAYLOAD_TOO_LARGE) {
            return { kind: 'oversize' };
        }
        if (!response.ok) {
            // On a retained manifest this is degradation, not a fault — the whole of
            // the `retained` note on {@link LoadSeriesBundlesOptions}.
            if (retained)
                return { kind: 'rejected', status: response.status };
            throw new BundleFaultError(`bundle chunk ${chunk.index} returned HTTP ${response.status}`, 'permanent-status');
        }
        if (!response.body) {
            // A 2xx with nothing behind it: the status was fine, the connection was
            // not — which is the transport's problem however old the manifest is.
            throw new BundleFaultError(`bundle chunk ${chunk.index} returned no body`, 'transport');
        }
        const reader = response.body.getReader();
        const parser = new BundleStreamParser();
        try {
            for (;;) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                armStallTimer();
                if (!value || value.length === 0)
                    continue;
                for (const frame of parser.push(value)) {
                    const ordinal = chunk.offset + frame.index;
                    const image = images[ordinal];
                    if (!image) {
                        // More frames than the page has images at this offset — the manifest
                        // and the page disagree, which is a framing bug, not a bad member.
                        throw new BundleStreamError(`bundle chunk ${chunk.index} frame ${frame.index} maps past the page's images`);
                    }
                    if (frame.status !== BUNDLE_STATUS_OK || !seedDataSet(image, frame.bytes))
                        continue;
                    seededOrdinals.set(ordinal, image);
                    seededBytes.set(ordinal, frame.bytes.length);
                }
            }
        }
        catch (error) {
            // An abort is the caller's cancel or the stall timer, and a framing error
            // is a protocol fault; both are the chunk owner's to classify, so they go
            // up. Anything else raised while reading a body that already answered 2xx
            // is the connection dying under us — truncation, which is retryable.
            if (controller.signal.aborted || error instanceof BundleStreamError) {
                controller.abort(error);
                throw error;
            }
            return { kind: 'truncated' };
        }
        const outcome = parser.finish();
        // Died before the trailer, so nothing it did say can be trusted as final.
        // Whatever arrived whole stays cached and is credited to the retry.
        if (!outcome.complete)
            return { kind: 'truncated' };
        // A frame can arrive complete and still be rubble: the proxy commits a
        // member's length before it has the bytes, so one that disappears from GCS
        // mid-stream is zero-padded to keep later frames at their computed offsets,
        // and named here. Retract those regardless of how their frame looked.
        for (const badIndex of outcome.badIndexes) {
            const ordinal = chunk.offset + badIndex;
            const image = seededOrdinals.get(ordinal);
            if (!image)
                continue;
            unseedDataSet(image);
            seededOrdinals.delete(ordinal);
            seededBytes.delete(ordinal);
        }
        return { kind: 'complete' };
    }
    finally {
        clearStallTimer();
        slot.release();
    }
}
